<script lang="ts">
	import ArticleCard from "@components/ArticleCard.svelte";
	import Heading from "@components/common/Heading.svelte";
	import StructuredData from "@components/common/StructuredData.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import CoffeeStoreSection from "@components/sections/CoffeeStoreSection.svelte";
	import FathersFamilyJoinUs from "@components/sections/FathersFamilyJoinUs.svelte";
	import HeroSection from "@components/sections/HeroSection.svelte";
	import IntroSections from "@components/sections/IntroSections.svelte";
	import ProductSection from "@components/sections/ProductSection.svelte";
	import WeAreRoasterySection from "@components/sections/WeAreRoasterySection.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import type { Organization, WebSite } from "schema-dts";
	import { CategoryType } from "@core/schema/CategoryType.js";
	import { fathersCompany } from "@core/schema/fathersCompany.js";
	import { appName } from "@core/schema/names/appName.js";
	import { getCategoryPath } from "@core/schema/paths/getCategoryPath.js";
	import { getNewsAnchor } from "@core/schema/paths/getNewsAnchor.js";
	import { getPostsListingPath } from "@core/schema/paths/getPostsListingPath.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import translations from "./Homepage.translations.json" with { type: "json" };
	import type { HomepageProps } from "./HomepageProps.js";

	const translate = getTranslate(translations);

	const { newProducts, commonProducts, posts, subcategories, tenant }: HomepageProps = $props();
	const { WEB_URL } = getGlobalSettings();

	const organizationId = `${WEB_URL}/#organization`;

	const organizationSchema: Organization = {
		"@type": "Organization",
		"@id": organizationId,
		name: appName,
		legalName: fathersCompany.name,
		url: WEB_URL,
		logo: {
			"@type": "ImageObject",
			url: `${WEB_URL}/logo.png`,
			contentUrl: `${WEB_URL}/logo.png`,
		},
		telephone: "+420736532744",
		email: "info@fathers.cz",
		address: {
			"@type": "PostalAddress",
			streetAddress: "Mitrovická 128/66",
			postalCode: "72400",
			addressLocality: "Ostrava",
			addressCountry: "CZ",
		},
		sameAs: [
			"https://facebook.com/fatherscoffeeroastery/",
			"https://instagram.com/fathers_coffee_roastery/",
			"https://youtube.com/@fatherscoffee",
		],
	};

	const webSiteSchema: WebSite = {
		"@type": "WebSite",
		"@id": `${WEB_URL}/#website`,
		name: appName,
		url: WEB_URL,
	};
</script>

<StructuredData data={organizationSchema} />
<StructuredData data={webSiteSchema} />

<Layout transparentHeader>
	<HeroSection />
	<IntroSections />

	<ProductSection
		{subcategories}
		products={newProducts}
		title={translate("titleNewProducts")}
		anchor={getNewsAnchor(tenant.locale)}
	/>

	<ProductSection
		{subcategories}
		products={commonProducts}
		title="[{getCategoryPath(tenant.locale, CategoryType.Coffee)}][[{translate('titleClassicOffer')}]]"
		class="bg-fcrbeige"
	/>
	<CoffeeStoreSection />
	<FathersFamilyJoinUs />
	<WeAreRoasterySection />
	{#if posts.length > 0}
		<section class="py-14 sm:py-17 md:py-20 lg:py-23 xl:py-26">
			<Heading
				level={2}
				text="[{getPostsListingPath(tenant)}][[{translate('storiesHeading')}]]"
				class="heading-2 mb-8 md:mb-13 xl:mb-18"
			/>
			<div class="grid md:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-14 md:gap-y-18 w-fit mx-auto">
				{#each posts as post (post.id)}
					<ArticleCard headingLevel={2} {post} huge={false} />
				{/each}
			</div>
		</section>
	{/if}
</Layout>
